import { useState } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Aliases keyed by alphanumeric-only lowercase form
const COLUMN_ALIASES: Record<string, string> = {
  source: "source",
  fundmanager: "fund_manager", manager: "fund_manager",
  fundname: "fund_name", name: "fund_name",
  citicode: "citicode", citi: "citicode",
  sedol: "sedol",
  isin: "isin",
  currency: "currency", ccy: "currency",
  incacc: "inc_acc", incoracc: "inc_acc",
  status: "status",
  wrapsipp: "wrap_sipp", wrap: "wrap_sipp",
  aberdeensipp: "aberdeen_sipp", aberdeen: "aberdeen_sipp",
  cashisa: "cash_isa",
  stockssharesisa: "stocks_shares_isa", stocksandsharesisa: "stocks_shares_isa", ssisa: "stocks_shares_isa",
  offshorebond: "offshore_bond",
  onshorebond: "onshore_bond",
  personalportfolio: "personal_portfolio", pp: "personal_portfolio",
  amc: "amc", amcpct: "amc", amcpercent: "amc",
  ocf: "ocf", ocfpct: "ocf", ocfpercent: "ocf",
};

function normalizeKey(k: string) {
  // strip everything except a-z0-9 (handles "Citi Code", "OCF %", "AMC (%)", "Inc/Acc", etc.)
  return k.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function mapRow(raw: Record<string, unknown>) {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    const mapped = COLUMN_ALIASES[normalizeKey(k)];
    if (mapped) out[mapped] = v;
  }
  return out;
}

async function parseFile(file: File): Promise<Record<string, unknown>[]> {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "csv") {
    return new Promise((resolve, reject) => {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => resolve(res.data),
        error: (err) => reject(err),
      });
    });
  }
  if (ext === "xlsx" || ext === "xls") {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    // Read as 2D array so we can locate the real header row
    const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: null, blankrows: false });
    // Find first row containing a recognizable header (e.g. "Fund Name")
    let headerIdx = 0;
    for (let i = 0; i < Math.min(aoa.length, 20); i++) {
      const row = aoa[i] || [];
      const hit = row.some((c) => {
        if (c == null) return false;
        const k = normalizeKey(String(c));
        return COLUMN_ALIASES[k] === "fund_name";
      });
      if (hit) { headerIdx = i; break; }
    }
    const headers = (aoa[headerIdx] || []).map((h) => (h == null ? "" : String(h)));
    const out: Record<string, unknown>[] = [];
    for (let r = headerIdx + 1; r < aoa.length; r++) {
      const row = aoa[r] || [];
      const obj: Record<string, unknown> = {};
      for (let c = 0; c < headers.length; c++) {
        if (headers[c]) obj[headers[c]] = row[c] ?? null;
      }
      out.push(obj);
    }
    return out;
  }
  throw new Error("Unsupported file type. Use .csv or .xlsx");
}

export function UploadInvestableUniverseDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);

  const reset = () => {
    setFile(null);
    setRows(null);
    setPreviewCount(null);
    setParsing(false);
    setUploading(false);
  };

  const handleFile = async (f: File | null) => {
    setFile(f);
    setRows(null);
    setPreviewCount(null);
    if (!f) return;
    setParsing(true);
    try {
      const raw = await parseFile(f);
      const mapped = raw.map(mapRow).filter((r) => r.fund_name);
      setRows(mapped);
      setPreviewCount(mapped.length);
    } catch (e) {
      toast.error(`Parse failed: ${(e as Error).message}`);
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = async () => {
    if (!rows || rows.length === 0) return;
    setUploading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "replace-investable-universe",
        { body: { rows } }
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Replaced universe — ${(data as any).inserted.toLocaleString()} rows inserted`);
      setOpen(false);
      reset();
      onSuccess();
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Upload className="h-4 w-4 mr-2" /> Upload &amp; Replace
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Replace Investable Universe</DialogTitle>
          <DialogDescription>
            Upload a CSV or XLSX file. The entire universe will be replaced with the
            contents of this file. Expected columns: Source, Fund Manager, Fund Name,
            Citicode, SEDOL, ISIN, Currency, Inc/Acc, Status, Wrap SIPP, Aberdeen SIPP,
            Cash ISA, Stocks &amp; Shares ISA, Offshore Bond, Onshore Bond,
            Personal Portfolio, AMC, OCF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="universe-file">File</Label>
            <Input
              id="universe-file"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              disabled={parsing || uploading}
            />
          </div>

          {parsing && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Parsing file…
            </div>
          )}

          {previewCount !== null && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">
                  {previewCount.toLocaleString()} valid rows detected
                </div>
                <div>
                  This will <strong>delete all existing rows</strong> and replace them
                  with the file contents. This cannot be undone.
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!rows || rows.length === 0 || uploading || parsing}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Replacing…
              </>
            ) : (
              <>Replace {previewCount?.toLocaleString() ?? ""} rows</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
